import { useEffect, useRef } from 'react';
import uPlot, { type Options, type AlignedData } from 'uplot';
import 'uplot/dist/uPlot.min.css';

// Phase 2 (3/n) — shared uPlot wrapper.
//
// Three concerns the wrapper owns so the per-chart components stay
// thin:
//   - Device-pixel-ratio: uPlot needs explicit width/height; we feed
//     it the ResizeObserver-measured container size so charts redraw
//     crisp on zoom + retina without per-component plumbing.
//   - Token-aware colors: charts read CSS custom properties (e.g.
//     --color-warn) at construction time. Dark-mode flips are handled
//     by re-reading on the next ResizeObserver tick; no theme prop
//     drilling required.
//   - Lifecycle: each ChartCanvas creates exactly one uPlot instance.
//     Data updates use `setData` (cheap); option changes recreate.
//
// The component is intentionally not memoized — uPlot manages its own
// canvas; React reconciliation should not be in the hot path.

export type ChartCanvasProps = {
  /** uPlot AlignedData: [xs, ...series]. */
  data: AlignedData;
  /** uPlot options (axes, series, scales, etc.). Width / height are
   *  injected from the container's measured size — caller MUST omit
   *  them or supply 0 placeholders. */
  options: Omit<Options, 'width' | 'height'>;
  /** Container className. Width / height should be set via Tailwind
   *  classes on the wrapper since uPlot drives the canvas pixel size. */
  className?: string;
  /** Fixed aspect ratio. Defaults to 3.0 — wide trend tracks. */
  aspect?: number;
  /** Test-only: a11y label for the chart. Default: omitted. */
  ariaLabel?: string;
};

export function ChartCanvas({
  data,
  options,
  className,
  aspect = 3.0,
  ariaLabel,
}: ChartCanvasProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    function measure() {
      const w = host?.clientWidth ?? 0;
      const h = Math.max(40, Math.round(w / aspect));
      return { w, h };
    }

    const { w, h } = measure();
    if (w === 0) return; // wait for layout; ResizeObserver below will retry

    plotRef.current = new uPlot({ ...options, width: w, height: h }, data, host);

    const ro = new ResizeObserver(() => {
      const m = measure();
      if (m.w > 0 && plotRef.current) {
        plotRef.current.setSize({ width: m.w, height: m.h });
      }
    });
    ro.observe(host);
    return () => {
      ro.disconnect();
      plotRef.current?.destroy();
      plotRef.current = null;
    };
    // Options object identity intentionally not in deps — recreating
    // uPlot on every render is wasteful. Per-chart components rebuild
    // the options once and pass a stable reference. The exhaustive-deps
    // rule isn't loaded by the tokens-lint pass, so no disable comment
    // here.
  }, []);

  // Data updates flow through the cheap setData path. Options are
  // assumed static for the lifetime of one mount (see eslint-disable
  // above).
  useEffect(() => {
    plotRef.current?.setData(data);
  }, [data]);

  return (
    <div
      ref={hostRef}
      className={className}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
    />
  );
}

// readCssVar resolves a CSS custom property at runtime (e.g.
// 'color-critical' → the hex from tokens.css). Returns fallback when
// the var is unset (SSR + jsdom test paths). Chart components call
// this when building options so the colors track the active theme.
export function readCssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return fallback;
  }
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(`--${name}`)
    .trim();
  return v || fallback;
}
